import type { ZodType } from 'zod';
import { apiErrorSchema, type ErrorCode } from '@unit/shared';
import { auth } from './firebase.js';

/**
 * A failed API call, carrying the typed code the UI translates and the request
 * id the user can quote when something needs investigating.
 *
 * `details` carries whatever the server attached to the failure — the list of
 * open assignments blocking a deactivation, the per-field issues behind a
 * validation error. It is structured data, never a message to render directly:
 * the message always comes from the client's own catalogue, keyed by `code`.
 */
export class ApiClientError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly requestId: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * Every call goes through here, and every response is parsed with the same zod
 * schema the server validated against — so a contract drift fails loudly at the
 * boundary instead of surfacing as `undefined` three components deep.
 */
async function call<T>(
  method: Method,
  path: string,
  schema: ZodType<T>,
  body?: unknown,
  options: { anonymous?: boolean } = {},
): Promise<T> {
  const user = auth.currentUser;
  if (!user && !options.anonymous) {
    throw new ApiClientError('unauthenticated', '', 'No signed-in user');
  }

  const response = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(user && !options.anonymous ? { Authorization: `Bearer ${await user.getIdToken()}` } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) {
      throw new ApiClientError(
        parsed.data.error.code,
        parsed.data.error.requestId,
        parsed.data.error.message,
        parsed.data.error.details ?? {},
      );
    }
    throw new ApiClientError(
      'internal',
      response.headers.get('x-request-id') ?? '',
      `${response.status} ${response.statusText}`,
    );
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiClientError(
      'internal',
      response.headers.get('x-request-id') ?? '',
      `Response did not match the contract for ${path}: ${result.error.message}`,
    );
  }
  return result.data;
}

export const apiGet = <T>(path: string, schema: ZodType<T>): Promise<T> =>
  call('GET', path, schema);

export const apiPost = <T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> =>
  call('POST', path, schema, body ?? {});

export const apiPatch = <T>(path: string, schema: ZodType<T>, body: unknown): Promise<T> =>
  call('PATCH', path, schema, body);

export const apiPut = <T>(path: string, schema: ZodType<T>, body: unknown): Promise<T> =>
  call('PUT', path, schema, body);

export const apiDelete = <T>(path: string, schema: ZodType<T>): Promise<T> =>
  call('DELETE', path, schema);

/**
 * A binary GET (a PDF). Same bearer token, same error contract on failure;
 * on success the bytes come back as a Blob for the browser to open.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const user = auth.currentUser;
  if (!user) throw new ApiClientError('unauthenticated', '', 'No signed-in user');
  const response = await fetch(`/api${path}`, {
    headers: { Authorization: `Bearer ${await user.getIdToken()}` },
  });
  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      throw new ApiClientError(parsed.data.error.code, parsed.data.error.requestId, parsed.data.error.message, parsed.data.error.details ?? {});
    }
    throw new ApiClientError('internal', response.headers.get('x-request-id') ?? '', `${response.status} ${response.statusText}`);
  }
  return response.blob();
}

/** The public endpoints (story 33: self-registration): no token, signed in or not. */
export const publicGet = <T>(path: string, schema: ZodType<T>): Promise<T> => call('GET', path, schema, undefined, { anonymous: true });
export const publicPost = <T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> =>
  call('POST', path, schema, body, { anonymous: true });
